/**
 *  BOGOS (bogos.io) free gift app — product page placement.
 *
 *  BOGOS renders its own gift widgets wherever its app embed decides. The
 *  product page design wants one add-on row in a specific place: between the
 *  fit tip and the add to cart button. So its default product page components
 *  are hidden by freegifts-custom.css and the row is rendered here instead,
 *  into the container snippets/product-gift-upsell.liquid puts in that spot.
 *
 *  Per the BOGOS docs, customisation is driven by the events the app fires on
 *  `document`:
 *    fg-gifts:show-slider  detail: { addGiftToCartFunc, gifts }
 *    fg-gifts:gift-icon    detail: { icon, gifts, product: { handle, variant } }
 *
 *  Neither fires until the app embed is enabled on the theme, so with the
 *  embed off this file renders nothing and changes nothing.
 *
 *  https://docs.bogos.io/advanced-customization/advance-customization-for-devs/customize-using-events
 */
(function () {
  'use strict';

  /* ======================================================================
     BOGOS amounts are in major units, not cents.

     Two sources of money reach this file and they do not agree:
       - BOGOS's own event payload (GiftVariant.price, .original_price) counts
         in dollars — a $23 gift arrives as 23.
       - Shopify's product JSON counts in cents, as Shopify always does — the
         same gift reads 2300.

     Everything below works in cents, so only BOGOS's numbers are converted,
     through fromBogos(). Flip this to true if a future app version starts
     sending cents, which shows up as prices 100x too small.
     ====================================================================== */
  var BOGOS_AMOUNTS_IN_CENTS = false;

  /** A BOGOS amount, in cents. */
  function fromBogos(amount) {
    var value = Number(amount);
    if (!isFinite(value)) return 0;
    return BOGOS_AMOUNTS_IN_CENTS ? value : value * 100;
  }

  var state = { gifts: null, addGiftToCart: null, product: null };
  var products = {};

  /* -------------------------------------------------------------- helpers */

  function containers() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-gift-upsell]'));
  }

  function moneyFormat() {
    return (window.theme && window.theme.settings && window.theme.settings.money_with_currency_format) || '${{amount}}';
  }

  /** Shopify's four money placeholders, so the shop's own format is honoured. */
  function formatMoney(cents, format) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';

    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var placeholder = match ? match[1] : 'amount';
    var value;

    function withSeparators(number, decimals, thousands, decimalMark) {
      var fixed = (number / 100.0).toFixed(decimals);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimalMark + parts[1] : whole;
    }

    switch (placeholder) {
      case 'amount_no_decimals':
        value = withSeparators(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withSeparators(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withSeparators(cents, 0, '.', ',');
        break;
      default:
        value = withSeparators(cents, 2, ',', '.');
    }

    // A whole number of dollars reads better without ".00", which is how the
    // price appears in the design.
    value = value.replace(/([.,])00$/, '');
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /**
   * The gift product as Shopify knows it. BOGOS's payload carries neither a
   * description nor, for a cloned free gift, anything to price it by, so both
   * come from the shop's own product JSON.
   *
   * compare_at_price is what a free gift is worth: BOGOS clones the product at
   * price 0, so the only place a value can honestly come from is a compare-at
   * price set on that clone.
   */
  function loadProduct(handle, onLoad) {
    if (!handle) return;

    var entry = products[handle];
    if (entry && entry.loaded) {
      onLoad(entry.value);
      return;
    }
    // A row asks twice — once for its subtitle, once for its value — and two
    // rows can share a handle. Every caller waits on the one request.
    if (entry) {
      entry.waiting.push(onLoad);
      return;
    }

    entry = products[handle] = { loaded: false, waiting: [onLoad], value: { subtitle: '', compareAt: 0 } };

    var settle = function (value) {
      entry.value = value;
      entry.loaded = true;
      entry.waiting.forEach(function (callback) { callback(value); });
      entry.waiting = [];
    };

    fetch(window.theme.routes.root_url.replace(/\/$/, '') + '/products/' + handle + '.js')
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (data) {
        if (!data) {
          settle(entry.value);
          return;
        }
        var text = (data.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        var sentence = text.split(/(?<=[.!?])\s/)[0] || text;
        var variant = (data.variants || [])[0] || {};

        settle({
          subtitle: sentence.length > 90 ? sentence.slice(0, 89).trim() + '…' : sentence,
          compareAt: Number(variant.compare_at_price) > 0 ? Number(variant.compare_at_price) : 0
        });
      })
      .catch(function () { settle(entry.value); });
  }

  /** The cheapest variant that actually carries an offer price. */
  function offerVariant(gift) {
    if (!gift || !gift.variants || !gift.variants.length) return null;
    return gift.variants[0];
  }

  /**
   * A gift that costs nothing is not a decision, so it gets no checkbox: there
   * is nothing to opt into. It is shown as what it is — a reward, with what it
   * is worth.
   */
  function isFree(variant) {
    return !variant.price || Number(variant.price) <= 0;
  }

  /* --------------------------------------------------------------- render */

  function render() {
    var targets = containers();
    if (!targets.length) return;

    var gifts = (state.gifts || []).filter(function (gift) { return offerVariant(gift); });

    // A free gift wins the slot outright. Offering a paid add-on alongside
    // something the shopper is already getting for nothing muddles both, so
    // while any gift is free the paid rows are left out entirely.
    var free = gifts.filter(function (gift) { return isFree(offerVariant(gift)); });
    if (free.length) gifts = free;

    targets.forEach(function (container) {
      if (!gifts.length) {
        container.hidden = true;
        container.textContent = '';
        return;
      }

      container.hidden = false;
      container.textContent = '';

      gifts.forEach(function (gift) {
        container.appendChild(row(gift, container));
      });
    });
  }

  function row(gift, container) {
    var variant = offerVariant(gift);
    return isFree(variant) ? freeRow(gift, variant, container) : upsellRow(gift, variant, container);
  }

  /** The thumbnail, title and one-line pitch both presentations share. */
  function giftBody(gift, variant, container, titleText) {
    var thumb = document.createElement('span');
    thumb.className = 'gift-upsell__thumb';
    if (variant.thumbnail || gift.thumbnail) {
      var image = document.createElement('img');
      image.src = variant.thumbnail || gift.thumbnail;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 44;
      image.height = 44;
      thumb.appendChild(image);
    }

    var text = document.createElement('span');
    text.className = 'gift-upsell__text';

    var title = document.createElement('span');
    title.className = 'gift-upsell__title';
    title.textContent = titleText;

    var subtitle = document.createElement('span');
    subtitle.className = 'gift-upsell__subtitle';
    loadProduct(gift.handle, function (data) { subtitle.textContent = data.subtitle; });

    text.appendChild(title);
    text.appendChild(subtitle);
    return [thumb, text];
  }

  /**
   * A gift that costs nothing. No control, because there is nothing to choose:
   * it shows what arrives and what it is worth. Claiming it, where an offer
   * needs claiming, stays with the app's own popup, which is left alone.
   */
  function freeRow(gift, variant, container) {
    var wrapper = document.createElement('div');
    wrapper.className = 'gift-upsell__row gift-upsell__row--free';

    var price = document.createElement('span');
    price.className = 'gift-upsell__price gift-upsell__price--free';

    var free = document.createElement('span');
    free.className = 'gift-upsell__free';
    free.textContent = container.dataset.freeLabel || 'Free';
    price.appendChild(free);

    // What it is worth, so "free" means something. The app's own figure first;
    // a cloned gift usually has none, so the clone's compare-at price stands in.
    // If neither exists the row simply says Free — no value is invented.
    function showWorth(cents) {
      if (!(Number(cents) > 0) || price.querySelector('s')) return;

      var worthLabel = document.createElement('span');
      worthLabel.className = 'gift-upsell__sr';
      // Struck-through text is not announced, so the saving is spelled out.
      worthLabel.textContent = container.dataset.worthLabel || 'Worth';

      var was = document.createElement('s');
      was.innerHTML = formatMoney(cents, moneyFormat());

      price.insertBefore(was, free);
      price.insertBefore(worthLabel, was);
    }

    showWorth(fromBogos(variant.original_price));
    // Already cents: this one comes from Shopify, not from the app.
    loadProduct(gift.handle, function (data) { showWorth(data.compareAt); });

    giftBody(gift, variant, container, gift.title).forEach(function (node) { wrapper.appendChild(node); });
    wrapper.appendChild(price);
    return wrapper;
  }

  /** A paid add-on: a real choice, so a real checkbox. */
  function upsellRow(gift, variant, container) {
    var id = 'GiftUpsell-' + gift.id + '-' + variant.id;

    var label = document.createElement('label');
    label.className = 'gift-upsell__row';
    label.setAttribute('for', id);

    var checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'gift-upsell__checkbox';
    checkbox.id = id;
    checkbox.dataset.variantId = variant.id;
    checkbox.dataset.offerId = Array.isArray(gift.belongs_to_offer) ? gift.belongs_to_offer[0] : gift.belongs_to_offer || '';

    var price = document.createElement('span');
    price.className = 'gift-upsell__price';
    // innerHTML, not textContent: the shop's money format wraps the amount in
    // <span class="money">, which currency apps hook into. Everything inside is
    // the theme's own format plus a number this file formatted — no app string.
    price.innerHTML = '+' + formatMoney(fromBogos(variant.price), moneyFormat());

    label.appendChild(checkbox);
    giftBody(gift, variant, container, (container.dataset.addLabel || 'Add') + ' ' + gift.title)
      .forEach(function (node) { label.appendChild(node); });
    label.appendChild(price);

    checkbox.addEventListener('change', function () {
      onToggle(checkbox, gift, variant);
    });

    return label;
  }

  /* ---------------------------------------------------------------- cart */

  function onToggle(checkbox, gift, variant) {
    checkbox.disabled = true;

    var done = function () {
      checkbox.disabled = false;
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: {} }));
    };

    if (checkbox.checked) {
      // BOGOS owns adding a gift: it is the only thing that knows how to apply
      // the offer's discount to the line.
      if (typeof state.addGiftToCart !== 'function') {
        checkbox.checked = false;
        checkbox.disabled = false;
        return;
      }
      Promise.resolve(state.addGiftToCart(variant.id, 1, checkbox.dataset.offerId)).then(done, done);
      return;
    }

    // There is no documented BOGOS "remove gift", so unchecking goes through
    // Shopify's own cart endpoint.
    fetch(window.theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: String(variant.id), quantity: 0 })
    }).then(done, done);
  }

  /* --------------------------------------------------------------- events */

  var handlers = {
    'fg-gifts:show-slider': function (detail) {
      if (typeof detail.addGiftToCartFunc === 'function') {
        state.addGiftToCart = detail.addGiftToCartFunc;
      }
      if (detail.gifts) state.gifts = detail.gifts;
    },
    'fg-gifts:gift-icon': function (detail) {
      if (detail.gifts) state.gifts = detail.gifts;
      if (detail.product) state.product = detail.product;
    },
    // Not on the docs page, but the app publishes it as GIFT_UPDATED in
    // FGSECOMAPP.CUSTOM_EVENTS. It fires as the cart changes, which is what
    // keeps the row honest after a gift is claimed. Handled defensively: if it
    // carries no gifts, this is just a re-render.
    'fg-gifts:updated': function (detail) {
      if (detail.gifts) state.gifts = detail.gifts;
    }
  };

  Object.keys(handlers).forEach(function (name) {
    document.addEventListener(name, function (event) {
      if (!event.detail) return;
      handlers[name](event.detail);
      render();
    });
  });

  // This file is deferred, so the app could have fired before it ran. The
  // inline shim in snippets/freegifts-snippet.liquid records anything that
  // arrived first; replay it here.
  var buffered = window.bogosEventBuffer || [];
  buffered.forEach(function (entry) {
    if (handlers[entry.name] && entry.detail) handlers[entry.name](entry.detail);
  });
  window.bogosEventBuffer = null;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render);
  } else {
    render();
  }

  // Choosing a different size re-renders part of the product page; re-assert
  // the row in case its container was replaced.
  document.addEventListener('product:variant-change', render);
})();
